import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  Badge,
  Box,
  Button,
  ButtonDropdown,
  CollectionPreferences,
  Header,
  Icon,
  Modal,
  Pagination,
  SegmentedControl,
  SpaceBetween,
  Table,
  TextFilter,
  Toggle,
} from "@amzn/awsui-components-console";
import React, { useMemo, useState } from "react";

import { EvalCase, EvalCaseFormData } from "@/hooks/admin/useAdminEvalDataset";
import {
  ADMIN_DEFAULT_PAGE_SIZE,
  adminPageSizePreference,
} from "@/utils/adminTable";

import EvalCaseModal from "./EvalCaseModal";

interface EvalDatasetTableProps {
  cases: EvalCase[];
  loading: boolean;
  saving: boolean;
  seeding: boolean;
  onRefresh: () => void;
  onUpsert: (caseId: string, data: EvalCaseFormData) => Promise<boolean>;
  onDelete: (caseId: string) => Promise<boolean>;
  onSeed: () => Promise<boolean>;
}

const DIFFICULTY_COLORS: Record<string, "green" | "blue" | "red"> = {
  simple: "green",
  moderate: "blue",
  complex: "red",
};

const formatRelativeTime = (isoString: string | null): string => {
  if (!isoString) return "\u2014";
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays}d ago`;
};

const EvalDatasetTable: React.FC<EvalDatasetTableProps> = ({
  cases,
  loading,
  saving,
  seeding,
  onRefresh,
  onUpsert,
  onDelete,
  onSeed,
}) => {
  const [modalCase, setModalCase] = useState<EvalCase | null>(null);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [modalVisible, setModalVisible] = useState(false);
  const [deleteConfirmCase, setDeleteConfirmCase] = useState<EvalCase | null>(null);
  const [seedConfirmVisible, setSeedConfirmVisible] = useState(false);
  const [filterSegment, setFilterSegment] = useState("all");
  const [preferences, setPreferences] = useState({
    pageSize: ADMIN_DEFAULT_PAGE_SIZE,
  });

  // Filter by segment
  const visibleCases = useMemo(() => {
    switch (filterSegment) {
      case "disabled":
        return cases.filter((c) => c.disabled);
      default:
        return cases;
    }
  }, [cases, filterSegment]);

  const {
    items,
    collectionProps,
    filterProps,
    paginationProps,
  } = useCollection(visibleCases, {
    filtering: {
      filteringFunction: (item, text) => {
        const q = text.toLowerCase();
        return (
          item.case_id.toLowerCase().includes(q) ||
          item.turns.some((t) => t.toLowerCase().includes(q)) ||
          item.tags.some((t) => t.toLowerCase().includes(q))
        );
      },
    },
    pagination: { pageSize: preferences.pageSize },
    // `isDescending` omitted: it defaults to false, so cases open in ascending
    // Test ID order rather than most-recently-modified first.
    sorting: { defaultState: { sortingColumn: { sortingField: "case_id" } } },
  });

  const handleAdd = () => {
    setModalCase(null);
    setModalMode("add");
    setModalVisible(true);
  };

  const handleEdit = (evalCase: EvalCase) => {
    setModalCase(evalCase);
    setModalMode("edit");
    setModalVisible(true);
  };

  const handleModalSave = async (caseId: string, data: EvalCaseFormData) => {
    const success = await onUpsert(caseId, data);
    if (success) {
      setModalVisible(false);
    }
    return success;
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmCase) {
      await onDelete(deleteConfirmCase.case_id);
      setDeleteConfirmCase(null);
    }
  };

  const handleSeedConfirm = async () => {
    await onSeed();
    setSeedConfirmVisible(false);
  };

  const handleToggleDisabled = async (evalCase: EvalCase, disabled: boolean) => {
    const data: EvalCaseFormData = {
      tags: evalCase.tags,
      turns: evalCase.turns,
      expected_sql: evalCase.expected_sql,
      expected_answer: evalCase.expected_answer,
      difficulty: evalCase.difficulty,
      disabled,
      out_of_scope: evalCase.out_of_scope,
      allow_non_deterministic: evalCase.allow_non_deterministic,
      score_all_turns: evalCase.score_all_turns,
      known_flaky: evalCase.known_flaky,
      source_tab: evalCase.source_tab,
    };
    await onUpsert(evalCase.case_id, data);
  };

  const columnDefinitions = [
    {
      id: "case_id",
      header: "Test ID",
      sortingField: "case_id",
      cell: (item: EvalCase) => (
        <div style={{ minWidth: 330 }}>
          <Button variant="link" onClick={() => handleEdit(item)}>
            {item.case_id}
          </Button>
        </div>
      ),
      width: 320,
    },
    {
      id: "question",
      header: "Question",
      cell: (item: EvalCase) => (
        <div style={{ minWidth: 300, wordWrap: "break-word", whiteSpace: "normal" }}>
          {item.turns[0] || ""}
        </div>
      ),
    },
    {
      id: "difficulty",
      header: "Difficulty",
      sortingField: "difficulty",
      cell: (item: EvalCase) => (
        <Badge color={DIFFICULTY_COLORS[item.difficulty] ?? "blue"}>
          {item.difficulty}
        </Badge>
      ),
    },
    {
      id: "turns",
      header: "Turns",
      sortingComparator: (a: EvalCase, b: EvalCase) => a.turns.length - b.turns.length,
      cell: (item: EvalCase) => `${item.turns.length} turn${item.turns.length > 1 ? "s" : ""}`,
    },
    {
      id: "sql_count",
      header: "SQLs",
      cell: (item: EvalCase) => `${item.expected_sql.length}`,
    },
    {
      id: "disabled",
      header: "Disabled",
      sortingField: "disabled",
      cell: (item: EvalCase) => (
        <div style={{ textAlign: "center" }}>
          <Toggle
            checked={item.disabled}
            onChange={({ detail }) => handleToggleDisabled(item, detail.checked)}
            ariaLabel={`Disable case: ${item.case_id}`}
          />
        </div>
      ),
    },
    {
      id: "out_of_scope",
      header: "Out of Scope",
      cell: (item: EvalCase) =>
        item.out_of_scope ? (
          <div style={{ textAlign: "center" }}>
            <Icon name="status-positive" variant="success" />
          </div>
        ) : null,
    },
    {
      id: "lastModified",
      header: "Last Modified",
      sortingField: "lastModifiedAt",
      cell: (item: EvalCase) => (
        <div style={{ textAlign: "center" }} title={item.lastModifiedAt ?? undefined}>
          {formatRelativeTime(item.lastModifiedAt)}
        </div>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: (item: EvalCase) => (
        <SpaceBetween direction="horizontal" size="xxs">
          <Button
            variant="icon"
            iconName="edit"
            ariaLabel={`Edit case ${item.case_id}`}
            onClick={() => handleEdit(item)}
          />
          <Button
            variant="icon"
            iconName="remove"
            ariaLabel={`Delete case ${item.case_id}`}
            onClick={() => setDeleteConfirmCase(item)}
          />
        </SpaceBetween>
      ),
    },
  ];

  return (
    <>
      <Table
        {...collectionProps}
        data-testid="eval-dataset-table"
        columnDefinitions={columnDefinitions}
        items={items}
        loading={loading}
        loadingText="Loading eval cases..."
        trackBy="case_id"
        variant="full-page"
        stickyHeader
        header={
          <Header
            variant="h1"
            counter={`(${cases.length})`}
            description={`${cases.filter((c) => !c.disabled).length} active \u00b7 ${cases.filter((c) => c.disabled).length} disabled`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <ButtonDropdown
                  items={[
                    {
                      id: "seed",
                      text: "Seed from baseline",
                      description: "Load the default test cases from the packaged dataset",
                    },
                  ]}
                  onItemClick={({ detail }) => {
                    if (detail.id === "seed") setSeedConfirmVisible(true);
                  }}
                >
                  Actions
                </ButtonDropdown>
                <Button iconName="refresh" onClick={onRefresh} loading={loading}>
                  Refresh
                </Button>
                <Button variant="primary" onClick={handleAdd}>
                  Add case
                </Button>
              </SpaceBetween>
            }
          >
            Agent Evaluation Dataset
          </Header>
        }
        filter={
          <SpaceBetween direction="horizontal" size="xs">
            <SegmentedControl
              selectedId={filterSegment}
              onChange={({ detail }) => setFilterSegment(detail.selectedId)}
              options={[
                { id: "all", text: "All" },
                { id: "disabled", text: "Disabled" },
              ]}
            />
            <TextFilter {...filterProps} filteringPlaceholder="Search cases..." />
          </SpaceBetween>
        }
        pagination={<Pagination {...paginationProps} />}
        preferences={
          <CollectionPreferences
            title="Preferences"
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            preferences={preferences}
            pageSizePreference={adminPageSizePreference("cases")}
            onConfirm={({ detail }) =>
              setPreferences({ pageSize: detail.pageSize ?? ADMIN_DEFAULT_PAGE_SIZE })
            }
          />
        }
      />

      {/* Add/Edit Modal */}
      {modalVisible && (
        <EvalCaseModal
          visible={modalVisible}
          mode={modalMode}
          evalCase={modalCase}
          saving={saving}
          onDismiss={() => setModalVisible(false)}
          onSave={handleModalSave}
        />
      )}

      {/* Seed Confirmation Modal */}
      {seedConfirmVisible && (
        <Modal
          visible={true}
          onDismiss={() => setSeedConfirmVisible(false)}
          header="Seed dataset from baseline"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => setSeedConfirmVisible(false)} disabled={seeding}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSeedConfirm} loading={seeding}>
                  Seed
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="s">
            <Box>
              This will load the default set of test cases from the packaged baseline
              dataset into this environment.
            </Box>
            <Box>
              <strong>What happens:</strong>
            </Box>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Cases with matching Test IDs will be <strong>overwritten</strong> with baseline values.</li>
              <li>Cases you added manually (not in the baseline) will <strong>not be deleted</strong>.</li>
              <li>No duplicates are created — each Test ID is unique.</li>
            </ul>
            <Box color="text-body-secondary">
              This is typically used once when setting up a new environment. If you have
              made custom edits to existing cases, they will be reset to baseline values.
            </Box>
          </SpaceBetween>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmCase && (
        <Modal
          visible={true}
          onDismiss={() => setDeleteConfirmCase(null)}
          header="Delete evaluation case"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => setDeleteConfirmCase(null)}>Cancel</Button>
                <Button variant="primary" onClick={handleDeleteConfirm}>
                  Delete
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="s">
            <Box>
              Are you sure you want to permanently delete case{" "}
              <strong>{deleteConfirmCase.case_id}</strong>?
            </Box>
            <Box color="text-body-secondary">
              This cannot be undone. Consider disabling the case instead if you
              want to exclude it from eval runs without losing it.
            </Box>
          </SpaceBetween>
        </Modal>
      )}
    </>
  );
};

export default EvalDatasetTable;
